import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { applySlip, getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SN_SAL_COL_REG_001_POP_001 from "@menu/SAL/SN_SAL_COL_REG_001/SN_SAL_COL_REG_001_POP_001";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

// 수금등록
const SN_SAL_COL_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [selectedTab, setSelectedTab] = useState("1");
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);

        const popup_apply = [{ target: "cd_bank", source: "fg_account" }];
        const grid2 = rtn.grid.grid2.map((col: any) => {
          let newCol = { ...col };
          if (col.cd_column === "no_mgmt") {
            newCol.popup_apply = popup_apply;
          }
          return newCol;
        });
        setColums({
          grid1: rtn.grid.grid1,
          grid2: grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    const modifiedRowsL2 = gridRefs.current["grid3"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsL2.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sal_col_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const newRow = {};
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onBeforeAddRowH = () => {
    try {
      const salSohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (salSohChanges.length > 0) return false;

      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        cd_dept: userInfo.cd_dept,
        cd_gp: userInfo.cd_gp,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_col: today,
        fg_trns: "1",
        cd_fin: "",
        am_col: 0,
        am_sales: 0,
        yn_docu: "N",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null || focusedNodeH.rowIndex == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      return gridRefs.current["grid2"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowL = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        no_line: maxNoLine + 1,
        no_col: focusedNodeH.data?.no_col,
        fg_col: "002",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRowD = () => {
    try {
      return gridRefs.current["grid3"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowD = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      return {
        cd_firm: userInfo.cd_firm,
        no_col: focusedNodeH.data?.no_col,
        am_col: 0,
        am_col_a: 0,
        am_col_n: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    if (focusedNodeH.data.no_docu) {
      await showDialog("alter", "회계 처리된 건은 삭제할 수 없습니다.");
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid3"].current?.deleteAllRows();
    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      return;
    }

    await onSave();
  };

  const onDeleteRow = async () => {
    gridRefs.current["grid1"].current.stopEditing();
    gridRefs.current["grid2"].current.stopEditing();
    gridRefs.current["grid3"].current.stopEditing();

    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    if (focusedNodeH.data.no_docu) {
      await showDialog("alter", "회계 처리된 건은 삭제할 수 없습니다.");
      return;
    }

    // 행삭제 후 수금액이 반제액보다 적으면 안됨
    let selectedAmColSum = 0;
    gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
      if (node.data && node.isSelected()) {
        selectedAmColSum += Number(node.data.am_col || 0);
      }
    });

    const headerAmCol = focusedNodeH.data?.am_col; // 헤더 수금액
    const headerAmSales = focusedNodeH.data?.am_sales; // 헤더 반제액
    const remainAmCol = headerAmCol - selectedAmColSum;
    if (remainAmCol < headerAmSales) {
      await showDialog(
        "alter",
        "수금액은 반제액보다 작을 수 없습니다.\n반제정보를 먼저 삭제해주세요."
      );
      return;
    }

    gridRefs.current["grid2"].current?.deleteSelectedRows();
    setFooterDataToRowL(false);
  };

  const onDeleteRowD = async () => {
    gridRefs.current["grid1"].current.stopEditing();
    gridRefs.current["grid2"].current.stopEditing();
    gridRefs.current["grid3"].current.stopEditing();

    if (gridRefs.current["grid3"].current?.getDisplayedRowCount() < 1) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    if (focusedNodeH.data.no_docu) {
      await showDialog("alter", "회계 처리된 건은 삭제할 수 없습니다.");
      return;
    }

    gridRefs.current["grid3"].current?.deleteSelectedRows();
    setFooterDataToRowD();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();
      gridRefs.current["grid3"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const salColHChanges = gridRefs.current["grid1"].current.getChange();
      const salColLChanges = gridRefs.current["grid2"].current.getChange();
      const salColDChanges = gridRefs.current["grid3"].current.getChange();

      const headerInserts = salColHChanges.insert ?? [];
      const lineInserts = salColLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        salColHChanges.modifiedRowcount === 0 &&
        salColLChanges.modifiedRowcount === 0 &&
        salColDChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      for (let row of lineInserts) {
        if (!row.fg_col) continue;

        const fg_col = row.fg_col.toString();
        const isEmpty = (value: any) => !value?.toString().trim();
        switch (fg_col) {
          case "003":
          case "004":
            if (isEmpty(row.no_mgmt)) {
              showDialog(
                "alter",
                "수금내역의 어음(수표)번호(관리번호)가 없습니다."
              );
              return false;
            }

            if (isEmpty(row.fg_issue)) {
              showDialog("alter", "수금내역의 자/타수를 입력하시기 바랍니다.");
              return false;
            }

            if (isEmpty(row.cd_bank)) {
              showDialog("alter", "금융기관을 입력하시기 바랍니다.");
              return false;
            }

            if (isEmpty(row.nm_issue)) {
              showDialog("alter", "발행인을 입력하시기 바랍니다.");
              return false;
            }

            if (isEmpty(row.dt_due)) {
              showDialog("alter", "만기일을 입력하시기 바랍니다.");
              return false;
            }
            break;
          case "005":
            if (isEmpty(row.no_mgmt)) {
              showDialog(
                "alter",
                "수금내역의 어음(수표)번호(관리번호)가 없습니다."
              );
              return false;
            }
            if (isEmpty(row.cd_bank)) {
              showDialog("alter", "금융기관을 입력하시기 바랍니다.");
              return false;
            }
            if (isEmpty(row.nm_issue)) {
              showDialog("alter", "발행인을 입력하시기 바랍니다.");
              return false;
            }
            break;
          case "006":
          case "010":
            if (isEmpty(row.cd_bank)) {
              showDialog("alter", "금융기관을 입력하시기 바랍니다.");
              return false;
            }
            break;
        }
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_col",
        cd_module: "SAL",
        no_line: 10,
        cd_prefix: "COL",
        dt: focusedRowNodeH.data.dt_col,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [salColHChanges, salColLChanges],
        cd_menu: Menuprops.cd_menu,
      });
      gridRefs.current["grid3"].current?.forEachNode((node: any) => {
        if (node.data.no_col == "" || node.data.no_col == null) {
          node.setDataValue("no_col", noSlip);
        }
      });

      const saveData = {
        collectH: salColHChanges,
        collectL: salColLChanges,
        collectD: salColDChanges,
      };
      const ok = await fnSave("SN_SAL_COL_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid2"].current.resetUpdate(true);
        gridRefs.current["grid1"].current.resetUpdate(true);
        gridRefs.current["grid3"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid3"].current?.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid3"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id === "addRow") {
      onAddRow();
    }
    if (id === "deleteRow") {
      onDeleteRow();
    }
  };

  const onGrid3BtnsClick = async (id: string) => {
    if (id === "addRow") {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null || focusedNodeH.rowIndex == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      if (gridRefs.current["grid2"].current.getDisplayedRowCount() < 1) return;

      const changedH = gridRefs.current["grid1"].current.getChange();
      const changedL = gridRefs.current["grid2"].current.getChange();
      if (changedH.modifiedRowcount > 0 || changedL.modifiedRowcount > 0) {
        showDialog("alter", "수정중인 건이 있습니다.");
        return false;
      }

      if (focusedNodeH.data.am_col - (focusedNodeH.data?.am_sales ?? 0) < 1) {
        showDialog("alter", "반제시킬 정상수금이 없습니다.");
        return false;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: focusedNodeH.data.cd_cust,
        nm_cust_sort: focusedNodeH.data.nm_cust_sort,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        fg_trns: "1",
      };

      const component = (
        <SN_SAL_COL_REG_001_POP_001 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "매출적용",
        children: component,
        width: "950px",
        height: "650px",
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      if (headerResult) {
        headerResult.map((result: any) => {
          const addedNode = onAddRowD();
          addedNode.setDataValue("no_sales", result.no_sales);
          addedNode.setDataValue("dt_sales", result.dt_sales);
          addedNode.setDataValue("am_target", result.am_k + result.am_vat);
          addedNode.setDataValue("am_col", result.am_col);
          addedNode.setDataValue("am_remin", result.am_remin);
        });
      }
    }
    if (id === "deleteRow") {
      onDeleteRowD();
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_col = selectedRow.no_col;
      const dt_col = selectedRow.dt_col || "";
      setSelectedDocu(no_col);
      setSelectedDtDocu(dt_col);
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      gridRefs.current["grid3"].current.selectAndFocusRow(0);
    } else {
      return;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_cust") {
      if (rowData.cd_cust !== rowData.cd_cust_sales) {
        rowNode.setDataValue("cd_cust_sale", rowData.cd_cust);
      }
    }
  };

  const onCellValueChangedL = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "am_col_n" || colId === "am_col_a") {
      const am_col_a = rowData.am_col_a ?? 0;
      const am_col_n = rowData.am_col_n ?? 0;
      rowNode.setDataValue("am_col", am_col_a + am_col_n);
      gridRefs.current["grid2"].current.calculateTotals();
      setFooterDataToRowL(false);
    }
  };

  const onCellValueChangedD = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "am_col") {
      const am_target = rowData.am_target;
      const am_col = rowData.am_col;
      rowNode.setDataValue(
        "am_remin",
        am_target - am_col > 0 ? am_target - am_col : 0
      );
      gridRefs.current["grid3"].current.calculateTotals();
      setFooterDataToRowD();
    }
  };

  const setFooterDataToRowL = (noChangeFlag: boolean) => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerDataL = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      if (noChangeFlag) {
        focusedNodeH.setDataValue("am_col", footerDataL.am_col, "noChange");
        focusedNodeH.setDataValue("am_col_a", footerDataL.am_col_a, "noChange");
      } else {
        focusedNodeH.setDataValue("am_col", footerDataL.am_col);
        focusedNodeH.setDataValue("am_col_a", footerDataL.am_col_a);
      }
    }
  };

  const setFooterDataToRowD = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerDataD = gridRefs.current["grid3"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am_sales", footerDataD.am_col);
    }
  };

  const onTabChanged = (index: any) => {
    setSelectedTab(index);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBtnAddClick: onGrid3BtnsClick,
          onBtnDeleteClick: onGrid3BtnsClick,
          onBeforeAddRow: onBeforeAddRowD,
          onCellValueChanged: onCellValueChangedD,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "수금정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["no_col"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnTab
            titles={["수금내역", "반제내역"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            header={[
              <SnTabHeader
                onBtnAddClick={onGrid2BtnsClick}
                onBtnDeleteClick={onGrid2BtnsClick}
              />,

              <SnTabHeader
                onBtnAddClick={onGrid3BtnsClick}
                onBtnDeleteClick={onGrid3BtnsClick}
              />,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "multiple" }}
                events={grids.grid2.events}
              />,
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ rowSelection: "multiple" }}
                events={grids.grid3.events}
              />,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
          selectedDtDocu={selectedDtDocu}
        />
      </div>
    </div>
  );
});

export default SN_SAL_COL_REG_001;
